/**
 * Actycrea: send.esm.js (ES6) — reCAPTCHA Enterprise only
 * MIT
 */

import { PREFIX } from '../helpers/config.js';
import { forceDownload } from '../helpers/helpers.js';
import { Toasts } from '../helpers/toasts.js';
import { setActiveLoading , removeActiveLoading } from '../helpers/spinner.js';

const SELECTOR            = `data-${PREFIX}-mysd`;
const SELECTOR_DATA       = `[${SELECTOR}]`;

const SELECTOR_EQUAL      = `data-${PREFIX}-equal`;
const SELECTOR_SUBMIT     = '[type="submit"]';
const SELECTOR_EMAIL      = 'input[type="email"]';

const CLASS_HIDDEN        = 'd-none';

const SFMC_REDIRECT_DELAY = 2000;

const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.es', 'yahoo.com.mx', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
  'outlook.com', 'outlook.es', 'outlook.com.mx',
  'hotmail.com', 'hotmail.es', 'hotmail.com.mx', 'hotmail.co.uk', 'hotmail.fr',
  'live.com', 'live.com.mx', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aim.com',
  'pm.me',
  'gmx.com', 'gmx.es', 'gmx.de', 'web.de',
  'yandex.com', 'yandex.ru', 'mail.ru',
  'mail.com', 'inbox.com', 'fastmail.com', 'zoho.com', 'tutanota.com',
  'qq.com', '163.com', '126.com', 'naver.com',
  'terra.com', 'terra.com.mx', 'prodigy.net.mx',
  'orange.fr', 'laposte.net', 'wanadoo.es', 'wanadoo.fr',
];

const DEFAULTS = {
  ajax     : true ,
  interval : 5000 ,
  id       : '' ,
  cid      : 'contact' ,
};

// cache de loaders por sitekey
const _recaptchaLoaders = new Map( );

function _injectScript ( src ){
  return new Promise( ( resolve , reject ) => {
    const s = document.createElement( 'script' );
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = ( ) => resolve( );
    s.onerror = ( ) => reject( new Error( 'No se pudo cargar reCAPTCHA Enterprise' ) );
    document.head.appendChild( s );
  } );
}

async function loadRecaptchaEnterprise ( sitekey ){
  if ( _recaptchaLoaders.has( sitekey ) ) return _recaptchaLoaders.get( sitekey );
  const promise = ( async ( ) => {
    if ( typeof window.grecaptcha?.enterprise !== 'object' ) {
      await _injectScript( 'https://www.google.com/recaptcha/enterprise.js?render=' + encodeURIComponent( sitekey ) );
    }
    const api = window.grecaptcha?.enterprise;
    if ( !api ) throw new Error( 'grecaptcha.enterprise no disponible' );
    await new Promise( ( resolve ) => api.ready( resolve ) );
    if ( typeof api.execute !== 'function' ) throw new Error( 'grecaptcha.enterprise.execute no disponible' );
    return api;
  } )( );
  _recaptchaLoaders.set( sitekey , promise );
  return promise;
}

export class MySD {
  /**
   * @param {HTMLFormElement} element
   * @param {Object} config
   */
  constructor( element , config = { } ){
    if ( !( element instanceof HTMLFormElement ) ) throw new Error( 'MySD: element inválido' );

    this._element  = element;
    this._config   = { ...DEFAULTS , ...config };

    console.log( '%c=> send.esm.js => Activated' , 'background-color:green;color:#fff;padding: 5px 10px;' );
    console.table( this._config );

    this._rcpClientId = null;
    this._rcpContainer = null;
    this._formStartTriggered = false;

    this._submit   = element.querySelector( SELECTOR_SUBMIT );
    this._method   = String( element.getAttribute( 'method' ) || 'POST' ).toUpperCase( );
    this._action   = element.getAttribute( 'action' ) || ( window.wphack?.ajax_url || '' );

    this._onSubmit          = this._onSubmit.bind( this );
    this._onEmailFieldInput = this._onEmailFieldInput.bind( this );
    this._onFormFirstClick  = this._onFormFirstClick.bind( this );

    this._bind( );
  }

  /* =================
   * Listeners
   * ================*/
  _bind( ){
    this._element.addEventListener( 'submit' , this._onSubmit , false );
    this._element.addEventListener( 'input' , ( ev ) => {
      const t = ev.target;
      if ( t.matches( SELECTOR_EMAIL ) ) this._onEmailFieldInput( ev );
    } , false );
    this._element.addEventListener( 'change' , ( ev ) => {
      const t = ev.target;
      if ( t.matches( SELECTOR_EMAIL ) ) this._onEmailFieldInput( ev );
    } , false );
    this._element.addEventListener( 'click' , this._onFormFirstClick , false );
    this._element.addEventListener( 'focus' , this._onFormFirstClick , false );
  }

  /* =================
   * Validaciones
   * ================*/
  // Valida en tiempo real cualquier campo type="email" (email principal y repetir email):
  // dominio corporativo + (si aplica) que coincida con el campo que debe igualar.
  _onEmailFieldInput( ev ){
    this._validateEmailField( ev.target );
  }

  _validateEmailField( input ){
    const value = String( input.value || '' ).trim( ).toLowerCase( );
    const domain = value.includes( '@' ) ? value.split( '@' ).pop( ) : '';
    const isBlockedDomain = !!domain && BLOCKED_EMAIL_DOMAINS.includes( domain );

    let message = '';
    if ( isBlockedDomain ) {
      message = this._getCorporateEmailErrorMessage( );
    } else {
      const sel = input.getAttribute( SELECTOR_EQUAL );
      const target = sel ? this._element.querySelector( sel ) : null;
      if ( target && String( input.value ) !== String( target.value ) ) {
        message = 'Debes repetir el mismo valor.';
      }
    }

    input.setCustomValidity( message );
    this._toggleEmailFeedback( input , message , value );
  }

  _toggleEmailFeedback( input , message , value ){
    const feedback = input.nextElementSibling;
    const hasFeedbackEl = feedback && feedback.classList.contains( 'invalid-feedback' );
    if ( message && value ) {
      input.classList.add( 'is-invalid' );
      if ( hasFeedbackEl ) feedback.textContent = message;
    } else {
      input.classList.remove( 'is-invalid' );
      if ( hasFeedbackEl ) feedback.textContent = '';
    }
  }

  _getCorporateEmailErrorMessage( ){
    const COPY = {
      es: 'Solo emails corporativos son válidos.',
      en: 'Only corporate emails are valid.',
    };
    const lang = ( ( window.wphack?.lang_iso ) || document.documentElement.lang || 'en' ).slice( 0 , 2 ).toLowerCase( );
    return COPY[ lang ] || COPY.en;
  }

  _validateForm( ){
    const form = this._element;
    if ( form.classList.contains( 'needs-validation' ) && !form.classList.contains( 'was-validated' ) ) {
      form.classList.add( 'was-validated' );
    }
    return form.checkValidity( );
  }

  /* =================
   * Envío
   * ================*/
  async _onSubmit( ev ){
    ev.preventDefault( );
    ev.stopPropagation( );

    if ( !this._validateForm( ) ) return;

    // Disparar evento form_submit
    this.setDataLayerTracking( 'form_submit' );

    setActiveLoading( this._element );

    const sitekey = this._submit?.dataset?.sitekey || '';
    const action  = this._submit?.dataset?.action || 'submit';

    try {
      let token = '';
      if ( sitekey ) {
        const api = await loadRecaptchaEnterprise( sitekey );
        if ( !this._rcpContainer ) {
          this._rcpContainer = document.createElement( 'div' );
          this._rcpContainer.style.display = 'none';
          document.body.appendChild( this._rcpContainer );
        }
        if ( this._rcpClientId === null ) {
          this._rcpClientId = api.render( this._rcpContainer , {
            sitekey : sitekey ,
            size    : 'invisible' ,
            badge   : 'bottomright'
          } );
        }
        token = await api.execute( this._rcpClientId , { action : action } );
      }
      await this._send( token );
    } catch ( err ) {
      this._handleError( err );
    }
  }

  async _send( token ){
    const data = new FormData( this._element );
    if ( token ) data.append( 'token' , token );
    this.debug( data );
    
    // Verificar si es formulario SFMC
    const sfmcFormId = this._element.getAttribute( 'data-sfmc-form-id' );
    const isSFMCForm = !!sfmcFormId;
    
    console.log( 'DEBUG SFMC Form ID:', sfmcFormId );
    console.log( 'DEBUG isSFMCForm:', isSFMCForm );
    
    const res = await fetch( this._action , {
      method : this._method ,
      body   : data
    } );
    const payload = await res.json( );
    if ( !res.ok ) throw payload;
    
    // Debug de la respuesta
    console.log( 'DEBUG Response payload:', payload );
    console.log( 'DEBUG payload.data.sfmc:', payload?.data?.sfmc );
    
    // Si es formulario SFMC y recibimos datos SFMC, procesar con SFMC
    if ( isSFMCForm && payload?.data?.sfmc ) {
      console.log( 'DEBUG: Procesando con SFMC' );
      await this._handleSFMCSubmission( payload.data );
    } else {
      console.log( 'DEBUG: Procesando con Pardot', { isSFMCForm, hasSFMCData: !!payload?.data?.sfmc } );
      this._handleSuccess( payload );
    }
  }

  async _handleSFMCSubmission( responseData ){
    try {
      // Obtener uniqueId de SalesforceInteractions SDK
      let uniqueId = "";
      if ( typeof window.SalesforceInteractions !== "undefined" ) {
        uniqueId = window.SalesforceInteractions.getAnonymousId() || "";
      }
      
      // Preparar el payload para SFMC
      const sfmcPayload = {
        formData: responseData.payload,
        uniqueId: uniqueId
      };
      
      // Enviar a SFMC
      const sfmcRes = await fetch( responseData.sfmcUrl , {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify( sfmcPayload )
      } );
      
      if ( !sfmcRes.ok ) {
        const errorText = await sfmcRes.text();
        throw new Error( `SFMC Error (${sfmcRes.status}): ${errorText}` );
      }
      
      const sfmcResult = await sfmcRes.json();

      if ( sfmcResult?.isSuccess === false ) {
        const errorData = {
          data: {
            html: this._generateErrorToast( this._getGenericErrorMessage( ) )
          }
        };
        this._handleError( errorData );
        return;
      }

      const successData = {
        data: {
          html: responseData.html
        }
      };
      this._handleSuccess( successData );

      if ( sfmcResult?.redirectUrl ) {
        setTimeout( ( ) => {
          window.location.assign( sfmcResult.redirectUrl );
        }, SFMC_REDIRECT_DELAY );
      }

    } catch ( error ) {
      console.error( 'SFMC submission failed:', error );

      const errorData = {
        data: {
          html: this._generateErrorToast( this._getGenericErrorMessage( ) )
        }
      };
      this._handleError( errorData );
    }
  }

  _generateErrorToast( message ) {
    return `<div class="toast toast-variant-danger text-bg-danger" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="toast-body">${message}</div>
    </div>`;
  }

  _getGenericErrorMessage( ){
    const COPY = {
      es: 'Ocurrió un error al procesar tu solicitud. Por favor, inténtalo de nuevo más tarde.',
      en: 'There was an error processing your request. Please try again later.',
    };
    const lang = ( ( window.wphack?.lang_iso ) || document.documentElement.lang || 'en' ).slice( 0 , 2 ).toLowerCase( );
    return COPY[ lang ] || COPY.en;
  }


  /* =================
   * Respuestas
   * ================*/
  _handleSuccess( data ){
    console.info( '%cMySD => _handleSuccess => data => ' , 'color:green;' , data );
    
    // Disparar evento form_success
    this.setDataLayerTracking( 'form_success' );
    
    // Limpiar form_origin del sessionStorage
    sessionStorage.removeItem( 'form_origin' );
    
    if( data?.data?.url && data?.data?.filename ){
      forceDownload( data?.data?.url , data?.data?.filename );
    }
    Toasts.show( data?.data?.html );
    this._element.classList.add( 'sended' );
    this._element.classList.remove( 'was-validated' );
    this._element.reset( );
    this.resetRecaptcha( );
    removeActiveLoading( this._element );
    this.setDataLayer( 'success' );
  }

  _handleError( error ){
    console.error( '%cMySD => _handleError => error => ', 'color:red;' , error );
    Toasts.show( error?.data?.html );
    this.resetRecaptcha( );
    removeActiveLoading( this._element );
    this.setDataLayer( 'error' );
  }

  /* ============
   * Form Tracking
   * ===========*/
  _getFormType( ){
    const cid = this._config.cid || '';
    // Mapear contact_us a contact como se especificó
    if ( cid === 'contact_us' ) return 'contact';
    return cid;
  }

  _getFormOrigin( ){
    return sessionStorage.getItem( 'form_origin' ) || '';
  }

  _onFormFirstClick( ev ){
    if ( this._formStartTriggered ) return;
    
    const target = ev.target;
    if ( !target.matches( 'input, textarea, select, button' ) ) return;
    
    this._formStartTriggered = true;
    this.setDataLayerTracking( 'form_start' );
  }

  setDataLayerTracking( eventType ){
    const layer = {
      event: eventType,
      form_type: this._getFormType( ),
      form_location: window.location.pathname,
      form_origin: this._getFormOrigin( )
    };
    window.dataLayer = window.dataLayer || [ ];
    window.dataLayer.push( layer );
    console.log( 'MySD => setDataLayerTracking => ' + eventType + ' => ', layer );
  }

  /* ============
   * Utilidades
   * ===========*/
  debug( formData ){
    console.log( 'MySD => debug => formData => ' , formData );
    const out = { };
    for ( const [ k , v ] of formData.entries( ) ) out[ k ] = v;
    console.log( 'MySD => debug => out => ' , out );
  }

  resetRecaptcha( ){
    if ( typeof window.grecaptcha?.enterprise !== 'object' ) return;
    if ( this._rcpClientId === null ) return;
    window.grecaptcha.enterprise.reset( this._rcpClientId );
  }

  setDataLayer( status ) {
    const layer = {
      event      : 'formSubmit' ,
      formId     : this._config.cid ,
      formName   : this._config.cid.replace( /^./ , this._config.cid[ 0 ].toUpperCase() ) ,
      status     : status
    };
    window.dataLayer = window.dataLayer || [ ];
    window.dataLayer.push( layer );
    console.log( 'MySD => setDataLayer => dataLayer => ' , layer );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if ( el._mysdInstance ) return;
      let cfg = { };
      const raw = el.getAttribute( SELECTOR );
      if ( raw ) { try { cfg = JSON.parse( raw ); } catch { cfg = { }; } }
      el._mysdInstance = new MySD( el , cfg );
      instances.push( el._mysdInstance );
    } );
    return instances;
  }
}

export default MySD;
